import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { IconCheck, IconPlus } from '@tabler/icons-react';
import { MarketingShell } from '@/components/marketing/warm/marketing-shell';
import { AnswerCapsule } from '@/components/marketing/answer-capsule';
import { Btn, Eyebrow, H2 } from '@/components/marketing/warm/primitives';
import { setDocumentMeta, setCanonical, injectJsonLdList, buildWebPageSchema, buildFaqPageSchema, buildBreadcrumbSchema } from '@/lib/seo';
import { Chip, Window } from '@/routes/landing-v2/mockups';
import pagesJson from './v2-pages.json';

type VendorPlan = { plan: string; total: string; perAgent: string };
type PriceRow = { agents: number; tickets: number; kora: string; vendor: VendorPlan[] };
export type ComparePage = {
  url: string; vendor: string; title: string; meta: string; h1: string; lead: string; lastChecked: string; answer: string;
  verdict: { vendorFits: string[]; koraFits: string[] };
  pricing: { title: string; assumption: string; rows: PriceRow[]; breakEven: string; note?: string; sources: { label: string; url: string; checked: string }[] };
  features: { row: string; kora: string; vendor: string; status: 'sourced' | 'unverified' }[];
  switching: { title: string; body: string }[];
  koraWeak: string[];
  faq: { q: string; a: string }[];
  related: { href: string; title: string; line: string }[];
  cta: { title: string; subtitle: string };
};

export const COMPARE_V2_PAGES = pagesJson as unknown as ComparePage[];

const ink = { color: 'var(--warm-text)' };
const muted = { color: 'var(--warm-muted)' };
const card = { background: 'var(--warm-card)', border: '1px solid var(--warm-border)' };

/** Always-visible section wrapper (no scroll-reveal; nothing depends on JS). */
function Sec({ children, tint = false }: { children: React.ReactNode; tint?: boolean }) {
  return (
    <section style={tint ? { background: 'var(--warm-surface)' } : undefined}>
      <div className="mx-auto max-w-[1200px] px-5 py-14 sm:px-8 md:py-20">{children}</div>
    </section>
  );
}

/** "$1,725 a month" → 1725; anything else (for example "See note") → null, so no bar and no invented total. */
const dollars = (s: string): number | null => {
  const m = s.match(/^\$([\d,]+(?:\.\d+)?)/);
  return m ? Number(m[1].replace(/,/g, '')) : null;
};

function CostWindow({ row, vendor }: { row: PriceRow; vendor: string }) {
  const items: { label: string; total: string; n: number | null; kora?: boolean }[] = [
    { label: 'Kora', total: `${row.kora} a month`, n: dollars(row.kora), kora: true },
    ...row.vendor.map((v) => ({ label: `${vendor} ${v.plan}`, total: v.total, n: dollars(v.total) })),
  ];
  const max = Math.max(1, ...items.map((i) => i.n ?? 0));
  return (
    <Window title={`${row.agents} agents, ${row.tickets.toLocaleString('en-US')} tickets a month`} label="Calculation, not a customer">
      <ul className="space-y-3.5">
        {items.map((i) => (
          <li key={i.label}>
            <div className="flex items-baseline justify-between gap-3 text-xs" style={muted}>
              <span style={i.kora ? { ...ink, fontWeight: 500 } : undefined}>{i.label}</span>
              <span className="tabular-nums" style={ink}>{i.total}</span>
            </div>
            {i.n != null && (
              <div className="mt-1 h-2 overflow-hidden rounded-full" style={{ background: 'var(--warm-inset)' }}>
                <div className="h-full rounded-full" style={{ width: `${Math.max(3, (i.n / max) * 100)}%`, background: i.kora ? 'var(--chip-green)' : 'var(--warm-text)', opacity: i.kora ? 1 : 0.7 }} />
              </div>
            )}
          </li>
        ))}
      </ul>
    </Window>
  );
}

export function ComparePageV2({ page }: { page: ComparePage }) {
  const mid = page.pricing.rows[1] ?? page.pricing.rows[0];

  useEffect(() => {
    setDocumentMeta(page.title, page.meta);
    setCanonical(page.url);
    return injectJsonLdList([
      buildWebPageSchema(page.url, page.title, page.meta),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app' },
        { name: 'Compare', url: 'https://usekora.app/compare' },
        { name: page.h1, url: `https://usekora.app${page.url}` },
      ]),
      buildFaqPageSchema(page.faq),
    ]);
  }, [page]);

  return (
    <MarketingShell>
      <section className="pt-28 pb-10 md:pt-32 md:pb-14">
        <div className="mx-auto grid max-w-[1200px] items-center gap-10 px-5 sm:px-8 lg:grid-cols-12 lg:gap-14">
          <div className="min-w-0 lg:col-span-6">
            <p className="warm-eyebrow mb-5">Compare</p>
            <h1 className="font-display text-[2.5rem] font-light leading-[1.05] tracking-tight md:text-[3.1rem]" style={ink}>{page.h1}</h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed" style={muted}>{page.lead}</p>
            <p className="mt-4 inline-flex rounded-md px-2 py-1 text-xs font-medium" style={{ background: 'var(--warm-inset)', ...muted }}>Prices checked {page.lastChecked}</p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Btn to="/auth">Start free <ArrowRight className="size-3.5" /></Btn>
              <Link to="/pricing" className="inline-flex min-h-[44px] items-center px-3 text-sm" style={muted}>See pricing</Link>
            </div>
          </div>
          <div className="min-w-0 lg:col-span-6"><CostWindow row={mid} vendor={page.vendor} /></div>
        </div>
      </section>

      <div className="mx-auto max-w-3xl px-5 sm:px-8"><AnswerCapsule>{page.answer}</AnswerCapsule></div>

      <Sec>
        <Eyebrow>Verdict</Eyebrow>
        <div className="grid gap-5 md:grid-cols-2">
          {[{ title: `Choose ${page.vendor} if`, items: page.verdict.vendorFits }, { title: 'Choose Kora if', items: page.verdict.koraFits }].map((c) => (
            <div key={c.title} className="rounded-2xl p-6" style={card}>
              <h2 className="font-display text-2xl font-light" style={ink}>{c.title}</h2>
              <ul className="mt-4 space-y-2.5">
                {c.items.map((i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed" style={muted}>
                    <IconCheck size={16} stroke={2.25} className="mt-0.5 shrink-0" style={{ color: 'var(--warm-ok)' }} />{i}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Sec>

      <Sec tint>
        <Eyebrow>Pricing</Eyebrow>
        <H2>{page.pricing.title}</H2>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed" style={muted}>{page.pricing.assumption}</p>
        <div className="mt-8 grid gap-5 lg:grid-cols-3">
          {page.pricing.rows.map((r) => <CostWindow key={r.agents} row={r} vendor={page.vendor} />)}
        </div>
        <p className="mt-6 max-w-3xl text-base leading-relaxed" style={ink}>{page.pricing.breakEven}</p>
        {page.pricing.note && <p className="mt-3 max-w-3xl rounded-xl p-4 text-sm leading-relaxed" style={{ background: 'var(--warm-card)', border: '1px solid var(--warm-border)', ...muted }}>{page.pricing.note}</p>}
        <p className="mt-4 text-xs" style={muted}>
          Sources:{' '}
          {page.pricing.sources.map((s, i) => (
            <span key={s.url}>{i > 0 && ' · '}<a href={s.url} rel="noopener noreferrer nofollow" target="_blank" className="underline">{s.label}</a> (checked {s.checked})</span>
          ))}
        </p>
      </Sec>

      <Sec>
        <Eyebrow>Features</Eyebrow>
        <H2>Side by side</H2>
        <div className="mt-8 overflow-x-auto rounded-2xl" style={card}>
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--warm-border)', ...muted }}>
                <th className="px-5 py-3 font-medium">Feature</th><th className="px-5 py-3 font-medium">Kora</th><th className="px-5 py-3 font-medium">{page.vendor}</th>
              </tr>
            </thead>
            <tbody>
              {page.features.map((f) => (
                <tr key={f.row} style={{ borderTop: '1px solid var(--warm-border)' }}>
                  <td className="px-5 py-3 align-top font-medium" style={ink}>{f.row}</td>
                  <td className="px-5 py-3 align-top" style={muted}>{f.kora}</td>
                  <td className="px-5 py-3 align-top" style={muted}>
                    {f.vendor}
                    {f.status === 'unverified' && <span className="ml-2 align-middle"><Chip tone="amber">not verified</Chip></span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Sec>

      <Sec tint>
        <Eyebrow>Switching</Eyebrow>
        <H2>Moving from {page.vendor} to Kora</H2>
        <ol className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {page.switching.map((s, i) => (
            <li key={s.title} className="rounded-2xl p-5" style={card}>
              <p className="flex items-center gap-2 text-sm font-medium" style={ink}>
                <span className="flex size-6 items-center justify-center rounded-full text-xs" style={{ background: 'var(--warm-inset)' }}>{i + 1}</span>{s.title}
              </p>
              <p className="mt-2 text-sm leading-relaxed" style={muted}>{s.body}</p>
            </li>
          ))}
        </ol>
      </Sec>

      <Sec>
        <div className="max-w-3xl rounded-2xl p-6" style={card}>
          <h2 className="font-display text-2xl font-light" style={ink}>Where Kora is weaker today</h2>
          <ul className="mt-4 space-y-2 text-sm" style={muted}>
            {page.koraWeak.map((i) => <li key={i} className="flex gap-2.5"><span className="mt-2 size-1 shrink-0 rounded-full" style={{ background: 'var(--warm-muted)' }} />{i}</li>)}
          </ul>
        </div>
      </Sec>

      <Sec tint>
        <Eyebrow>Common questions</Eyebrow>
        <div className="mt-4 grid gap-x-10 md:grid-cols-2">
          {page.faq.map((f, i) => (
            <details key={f.q} open={i === 0} className="group" style={{ borderBottom: '1px solid var(--warm-border)' }}>
              <summary className="flex min-h-[48px] cursor-pointer list-none items-center justify-between gap-4 py-3 text-[15px] font-medium" style={ink}>
                {f.q}<IconPlus size={18} stroke={1.5} className="shrink-0 transition-transform group-open:rotate-45" style={muted} />
              </summary>
              <p className="pb-4 text-sm leading-relaxed" style={muted}>{f.a}</p>
            </details>
          ))}
        </div>
      </Sec>

      <Sec>
        <Eyebrow>Related</Eyebrow>
        <div className="grid gap-4 md:grid-cols-3">
          {page.related.map((r) => (
            <Link key={r.href} to={r.href} className="rounded-2xl p-5" style={card}>
              <span className="block text-base font-medium" style={ink}>{r.title}</span>
              <span className="mt-1 block text-sm" style={muted}>{r.line}</span>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium" style={ink}>Read more <ArrowRight className="size-3.5" /></span>
            </Link>
          ))}
        </div>
        <div className="mt-10 grid items-center gap-6 rounded-3xl p-8 md:p-10 lg:grid-cols-2" style={{ background: 'var(--warm-surface)', border: '1px solid var(--warm-border)' }}>
          <div>
            <h2 className="font-display text-[1.75rem] font-light leading-tight md:text-[2.25rem]" style={ink}>{page.cta.title}</h2>
            <p className="mt-3 text-base" style={muted}>{page.cta.subtitle}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 lg:justify-end">
            <Btn to="/auth">Start free <ArrowRight className="size-3.5" /></Btn>
            <Link to="/compare" className="inline-flex min-h-[44px] items-center px-3 text-sm" style={muted}>All comparisons</Link>
          </div>
        </div>
      </Sec>
    </MarketingShell>
  );
}
